import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, CheckCheck, Inbox } from 'lucide-react';
import { API } from '@/config';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const POLL_INTERVAL_MS = 30000;

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const NotificationsBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`, authHeader());
      setUnreadCount(typeof data?.unread_count === 'number' ? data.unread_count : 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/notifications/unread-count`, authHeader());
        if (!cancelled) setUnreadCount(res.data?.unread_count || 0);
      } catch (e) {
        /* silent */
      }
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`, authHeader());
      setNotifications(res.data?.notifications || []);
      setUnreadCount(typeof res.data?.unread_count === 'number' ? res.data.unread_count : 0);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, authHeader());
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      /* silent */
    }
  };

  const handleClick = async (n) => {
    if (!n.is_read) {
      try {
        const res = await axios.post(`${API}/notifications/${n.id}/read`, {}, authHeader());
        setUnreadCount(res.data?.unread_count ?? Math.max(0, unreadCount - 1));
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      } catch (e) {
        /* silent */
      }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          data-testid="notifications-bell"
          className="relative p-2 rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none"
            >
              {badgeText}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <p className="text-sm font-semibold text-slate-800">Customer Inquiries</p>
            <p className="text-xs text-slate-500">
              {unreadCount > 0 ? `${unreadCount} unread` : 'You are all caught up'}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              data-testid="notifications-mark-all-read"
              className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Inbox className="h-8 w-8 mx-auto text-slate-300" />
              <p className="mt-2 text-sm text-slate-500">No notifications yet</p>
            </div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleClick(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 transition-colors hover:bg-slate-50 ${
                  n.is_read ? '' : 'bg-blue-50/60'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
                      n.is_read ? 'bg-transparent' : 'bg-blue-600'
                    }`}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold text-slate-700">{n.title}</span>
                    <span className="block text-sm text-slate-700 break-words">{n.message}</span>
                    <span className="block text-[11px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</span>
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsBell;
